import { useCallback } from "react";
import { NavigateOptions, To, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../components/hooks";
import { setIsSubscriptionRequiredOpen } from "../store/slices/appState";
import { SUBSCRIPTION_PATHS } from "../constants/routes";
import { SettingsView } from "../constants/settings";
import { isSubscriptionDisabled } from "../utils/platform";

const isSubscriptionDestination = (to: To, options?: NavigateOptions) => {
  const pathname = typeof to === "string" ? to : to.pathname ?? "";
  if (SUBSCRIPTION_PATHS.some((path) => pathname.startsWith(path))) return true;

  // The settings page picks its panel from router state, not from the path.
  const state = options?.state as { settingsView?: SettingsView } | undefined;
  return state?.settingsView === SettingsView.Subscription;
};

/**
 * Drop-in replacement for `useNavigate` on any navigation that can land on the
 * paywall, payment or subscription settings pages.
 *
 * When VITE_DISABLE_SUBSCRIPTION is enabled a navigation to one of those
 * destinations is swallowed and the "Subscription Required" popup is shown
 * instead. Every other destination navigates as usual.
 */
export const useSubscriptionNavigate = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  return useCallback(
    (...args: [to: To, options?: NavigateOptions]) => {
      if (isSubscriptionDisabled && isSubscriptionDestination(...args)) {
        dispatch(setIsSubscriptionRequiredOpen(true));
        return;
      }
      navigate(...args);
    },
    [navigate, dispatch],
  );
};
